import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, IconButton, Stack, TextField, Typography } from "@mui/material";
import { Bot, Paperclip, Send, Smile, UserRound, X } from "lucide-react";
import { publicApi } from "../api";
import { createVisitorSocket } from "../socket";
import { MessageTicks, uid } from "../ui";

// Customer-facing chat, rendered inside the iframe injected by widget.js.
// Standalone: no CRM login, no CRM axios instance, no internal data.

const EMOJIS = ["😊", "🙏", "👍", "🙂", "😔", "❤️", "👋", "✅"];
const storeKey = (k) => `jw_chat_${k}`;
// The app wraps localStorage (services/localStorageSecurity) and getItem may
// already return a parsed object, so accept both shapes.
const readStore = (k) => {
  try {
    const raw = window.localStorage.getItem(storeKey(k));
    if (!raw) return null;
    return typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }
};
const writeStore = (k, v) => {
  try {
    if (v) window.localStorage.setItem(storeKey(k), JSON.stringify(v));
    else window.localStorage.removeItem(storeKey(k));
  } catch {
    /* storage blocked (private mode / 3rd-party iframe) — session lives in memory */
  }
};

const upsert = (list, m) => {
  const i = list.findIndex((x) => x._id === m._id || (m.clientMsgId && x.clientMsgId === m.clientMsgId));
  if (i >= 0) {
    const n = [...list];
    n[i] = { ...n[i], ...m };
    return n;
  }
  return [...list, m];
};

export default function ChatWidget() {
  const { widgetKey } = useParams();
  const [params] = useSearchParams();
  const hostOrigin = params.get("hostOrigin") || "";
  const pageUrl = params.get("page") || "";
  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");
  const [session, setSession] = useState(() => readStore(widgetKey));
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [text, setText] = useState("");
  const [typing, setTyping] = useState("");
  const [sending, setSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [rated, setRated] = useState({});
  const sockRef = useRef(null);
  const bottom = useRef(null);
  const fileRef = useRef(null);
  const typingTimer = useRef(null);
  const color = config?.color || "#4F46E5";

  useEffect(() => {
    publicApi.widgetConfig(widgetKey, hostOrigin).then(setConfig).catch((err) => setError(err?.response?.data?.message || "Chat is unavailable"));
  }, [widgetKey, hostOrigin]);

  const start = async (e) => {
    e?.preventDefault();
    setError("");
    try {
      const r = await publicApi.startSession(widgetKey, { ...form, pageUrl, hostOrigin, visitorId: session?.visitorId });
      const s = { token: r.token, visitorId: r.visitorId, name: form.name || session?.name };
      writeStore(widgetKey, s);
      setSession(s);
      setConversation(r.conversation);
      setMessages(r.messages);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not start the chat");
    }
  };

  // resume a stored session
  const resume = useCallback(async () => {
    if (!session?.token) return;
    try {
      const r = await publicApi.messages(session.token);
      setConversation(r.conversation);
      setMessages(r.messages);
      if (r.conversation.status === "ended") setConversation(r.conversation);
    } catch {
      writeStore(widgetKey, null);
      setSession((s) => (s ? { visitorId: s.visitorId, name: s.name } : null));
    }
  }, [session?.token, widgetKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    resume();
  }, [resume]);

  // realtime
  useEffect(() => {
    if (!session?.token) return undefined;
    const s = createVisitorSocket(session.token);
    sockRef.current = s;
    s.on("message.created", (m) => {
      setMessages((l) => upsert(l, m));
      if (m.senderType !== "CUSTOMER") {
        setTyping("");
        s.emit("message.delivered", { messageIds: [m._id] });
        if (document.visibilityState === "visible") s.emit("message.read", { lastMessageId: m._id });
      }
    });
    s.on("message.read", (p) => setMessages((l) => l.map((m) => (m.senderType === "CUSTOMER" && m._id <= p.upToId ? { ...m, status: "read" } : m))));
    s.on("conversation.updated", (c) => setConversation((prev) => ({ ...prev, ...c })));
    s.on("typing.started", (p) => {
      setTyping(p.by === "AI" ? `${config?.botName || "Assistant"} is typing…` : `${p.name || "Agent"} is typing…`);
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => setTyping(""), 6000);
    });
    s.on("typing.stopped", () => setTyping(""));
    s.on("connect", async () => {
      // catch up on anything missed while disconnected
      const r = await publicApi.messages(session.token).catch(() => null);
      if (r) setMessages((l) => r.messages.reduce(upsert, l));
    });
    return () => s.close();
  }, [session?.token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [messages.length, typing]);

  useEffect(() => {
    const last = [...messages].reverse().find((m) => m.senderType !== "CUSTOMER");
    if (last && sockRef.current) sockRef.current.emit("message.read", { lastMessageId: last._id });
  }, [messages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (attachmentIds = []) => {
    const content = text.trim();
    if (!content && !attachmentIds.length) return;
    const clientMsgId = uid();
    const optimistic = { _id: `tmp_${clientMsgId}`, clientMsgId, senderType: "CUSTOMER", content, status: "sending", createdAt: new Date().toISOString(), attachments: [] };
    setMessages((l) => [...l, optimistic]);
    setText("");
    setSending(true);
    sockRef.current?.emit("typing.stopped");
    try {
      const m = await publicApi.send(session.token, { content, attachmentIds, clientMsgId });
      setMessages((l) => upsert(l.filter((x) => x._id !== optimistic._id), m));
    } catch (err) {
      setMessages((l) => l.map((x) => (x._id === optimistic._id ? { ...x, status: "failed" } : x)));
      if (err?.response?.status === 409) setConversation((c) => ({ ...c, status: "ended" }));
    } finally {
      setSending(false);
    }
  };

  const upload = async (files) => {
    if (!files?.length) return;
    try {
      const saved = await publicApi.upload(session.token, files);
      await send(saved.map((f) => f._id));
    } catch (err) {
      setError(err?.response?.data?.message || "Upload failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const openFile = async (a) => {
    try {
      const blob = await publicApi.fileBlob(session.token, a._id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setError("Could not open the file");
    }
  };

  const onType = (v) => {
    setText(v);
    const s = sockRef.current;
    if (!s) return;
    s.emit("typing.started");
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => s.emit("typing.stopped"), 2500);
  };

  const rate = async (token, rating) => {
    try {
      await publicApi.submitCsat(token, { rating });
      setRated((r) => ({ ...r, [token]: rating }));
    } catch {
      setRated((r) => ({ ...r, [token]: rating }));
    }
  };

  const newChat = () => {
    writeStore(widgetKey, null);
    setSession((s) => (s ? { visitorId: s.visitorId, name: s.name } : null));
    setMessages([]);
    setConversation(null);
  };

  const close = () => window.parent !== window && window.parent.postMessage({ type: "jw-chat-close" }, "*");

  const header = (
    <Box sx={{ bgcolor: color, color: "#fff", px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1.25 }}>
      <Box sx={{ width: 36, height: 36, borderRadius: "50%", bgcolor: "rgba(255,255,255,.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {conversation?.mode === "ai" || (!conversation && config?.aiEnabled) ? <Bot size={20} /> : <UserRound size={20} />}
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }} noWrap>{config?.title || "Support"}</Typography>
        <Typography sx={{ fontSize: "0.72rem", opacity: 0.9 }}>
          {conversation?.mode === "ai" ? `${config?.botName || "AI Assistant"} · Online` : conversation?.agentName ? `${conversation.agentName} · Support agent` : conversation?.status === "queued" ? "Connecting you to an agent…" : config?.online ? "Online" : "We'll reply by email"}
        </Typography>
      </Box>
      <IconButton size="small" onClick={close} sx={{ color: "#fff" }} aria-label="Close chat"><X size={18} /></IconButton>
    </Box>
  );

  if (error && !config) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  if (!config) return <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>;

  if (!session?.token || !conversation) {
    return (
      <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", fontFamily: "system-ui, sans-serif", bgcolor: "#fff", color: "#101828" }}>
        {header}
        <Box component="form" onSubmit={start} sx={{ p: 2.5, flexGrow: 1, overflowY: "auto" }}>
          <Typography sx={{ fontSize: "0.95rem", mb: 1 }}>{config.greeting}</Typography>
          {config.aiDisclosure && <Typography sx={{ fontSize: "0.75rem", color: "#667085", mb: 2 }}>{config.aiDisclosure}</Typography>}
          {!config.online && <Alert severity="info" sx={{ mb: 2 }}>{config.offlineMessage}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField fullWidth size="small" label="Your name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} sx={{ mb: 1.5 }} required />
          <TextField fullWidth size="small" type="email" label="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} sx={{ mb: 1.5 }} required={config.requireEmail} />
          {(config.requirePhone || !config.requireEmail) && <TextField fullWidth size="small" label="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} sx={{ mb: 1.5 }} required={config.requirePhone} />}
          <Button type="submit" fullWidth variant="contained" sx={{ bgcolor: color, textTransform: "none", fontWeight: 700, "&:hover": { bgcolor: color, filter: "brightness(.92)" } }}>Start chat</Button>
        </Box>
      </Box>
    );
  }

  const ended = conversation.status === "ended";
  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", fontFamily: "system-ui, sans-serif", bgcolor: "#F9FAFB", color: "#101828" }}>
      {header}
      <Box sx={{ flexGrow: 1, overflowY: "auto", px: 1.5, py: 1.5 }}>
        {messages.map((m) => {
          const mine = m.senderType === "CUSTOMER";
          if (m.senderType === "SYSTEM" && m.type !== "csat") {
            return <Typography key={m._id} sx={{ textAlign: "center", fontSize: "0.72rem", color: "#667085", my: 1, whiteSpace: "pre-wrap" }}>{m.content}</Typography>;
          }
          if (m.type === "csat") {
            const token = m.csatToken;
            return (
              <Box key={m._id} sx={{ textAlign: "center", my: 1.5, p: 1.5, bgcolor: "#fff", borderRadius: "12px", border: "1px solid #E4E7EC" }}>
                <Typography sx={{ fontSize: "0.85rem", mb: 1 }}>{m.content}</Typography>
                {token && !rated[token] ? (
                  <Stack direction="row" justifyContent="center" spacing={0.5}>{[1, 2, 3, 4, 5].map((n) => <IconButton key={n} size="small" onClick={() => rate(token, n)} aria-label={`${n} stars`}>{"⭐".repeat(1)}<Typography sx={{ fontSize: "0.7rem" }}>{n}</Typography></IconButton>)}</Stack>
                ) : <Typography sx={{ fontSize: "0.8rem", color: "#067647" }}>{rated[token] ? `Thanks for rating us ${rated[token]}/5!` : ""}</Typography>}
              </Box>
            );
          }
          return (
            <Box key={m._id} sx={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", my: 0.75 }}>
              <Box sx={{ maxWidth: "80%" }}>
                {!mine && <Typography sx={{ fontSize: "0.68rem", color: "#667085", mb: 0.25, ml: 0.5 }}>{m.senderType === "AI" ? `${config.botName} (AI)` : m.senderName || "Support"}</Typography>}
                <Box sx={{ px: 1.5, py: 1, borderRadius: mine ? "14px 14px 4px 14px" : "14px 14px 14px 4px", bgcolor: mine ? color : "#fff", color: mine ? "#fff" : "#101828", border: mine ? "none" : "1px solid #E4E7EC", overflowWrap: "anywhere" }}>
                  {m.content && <Typography sx={{ fontSize: "0.88rem", whiteSpace: "pre-wrap", lineHeight: 1.45 }}>{m.content}</Typography>}
                  {(m.attachments || []).map((a) => <Button key={a._id} size="small" onClick={() => openFile(a)} startIcon={<Paperclip size={13} />} sx={{ color: mine ? "#fff" : color, textTransform: "none", p: 0, mt: 0.5 }}>{a.fileName}</Button>)}
                </Box>
                <Stack direction="row" spacing={0.5} justifyContent={mine ? "flex-end" : "flex-start"} alignItems="center" sx={{ mt: 0.25, mx: 0.5 }}>
                  <Typography sx={{ fontSize: "0.62rem", color: "#98A2B3" }}>{new Date(m.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</Typography>
                  {mine && <MessageTicks status={m.status} />}
                </Stack>
              </Box>
            </Box>
          );
        })}
        {typing && <Typography sx={{ fontSize: "0.72rem", color: "#667085", fontStyle: "italic", ml: 1 }}>{typing}</Typography>}
        <div ref={bottom} />
      </Box>
      {error && <Alert severity="error" onClose={() => setError("")} sx={{ mx: 1.5 }}>{error}</Alert>}
      {ended ? (
        <Box sx={{ p: 1.5, borderTop: "1px solid #E4E7EC", bgcolor: "#fff", textAlign: "center" }}>
          <Typography sx={{ fontSize: "0.8rem", color: "#667085", mb: 1 }}>This chat has ended.</Typography>
          <Button variant="contained" size="small" sx={{ bgcolor: color, textTransform: "none" }} onClick={newChat}>Start a new chat</Button>
        </Box>
      ) : (
        <Box sx={{ borderTop: "1px solid #E4E7EC", bgcolor: "#fff", p: 1 }}>
          {showEmoji && <Stack direction="row" spacing={0.5} sx={{ px: 1, pb: 0.5 }}>{EMOJIS.map((e) => <IconButton key={e} size="small" onClick={() => { setText((t) => t + e); setShowEmoji(false); }}>{e}</IconButton>)}</Stack>}
          <Stack direction="row" spacing={0.5} alignItems="flex-end">
            <input ref={fileRef} type="file" hidden onChange={(e) => upload(e.target.files)} />
            <IconButton size="small" onClick={() => fileRef.current?.click()} aria-label="Attach file"><Paperclip size={18} /></IconButton>
            <IconButton size="small" onClick={() => setShowEmoji((v) => !v)} aria-label="Emoji"><Smile size={18} /></IconButton>
            <TextField fullWidth size="small" multiline maxRows={4} placeholder="Type your message…" value={text} onChange={(e) => onType(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
            <IconButton onClick={() => send()} disabled={sending || !text.trim()} sx={{ color }} aria-label="Send"><Send size={18} /></IconButton>
          </Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ px: 1, pt: 0.5 }}>
            {conversation.mode === "ai" ? <Button size="small" sx={{ textTransform: "none", fontSize: "0.72rem", p: 0 }} onClick={async () => { const c = await publicApi.requestAgent(session.token).catch(() => null); if (c) setConversation(c); }}>Talk to a human</Button> : <span />}
            <Button size="small" sx={{ textTransform: "none", fontSize: "0.72rem", p: 0, color: "#667085" }} onClick={async () => { await publicApi.end(session.token).catch(() => {}); setConversation((c) => ({ ...c, status: "ended" })); }}>End chat</Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
